import { getISOWeek, getISOWeekYear } from 'date-fns';
import type { PayRecord, Timecard } from '../types';
import { parseLocalDate } from './payrollPeriodLocal';

export interface StaffSalariedReportRow {
  employeeDisplay: string;
  yearNo: number;
  weekNo: number;
  weekStartDate: string;
  hoursWorked: number;
  setAgainstHours: number;
  hoursUnderOver: number;
  hoursInLieuDue: number;
}

/** Sum paid time from timecard days (same basis as weekly totals in Timecards UI). */
export function sumHoursWorkedFromTimecard(tc: Timecard): number {
  if (tc.days?.length) {
    return tc.days.reduce((s, d) => s + (Number(d.totalHours) || 0), 0);
  }
  return Number(tc.totalHours) || 0;
}

export function getApplicablePayRecordForWeek(records: PayRecord[], weekStartYmd: string): PayRecord | null {
  const ymd = weekStartYmd.slice(0, 10);
  const candidates = records.filter((pr) => {
    const eff = (pr.effectiveFrom || pr.fromDate || '1970-01-01').slice(0, 10);
    if (ymd < eff) return false;
    if (pr.isActive) return true;
    const end = pr.toDate?.slice(0, 10);
    if (end && ymd > end) return false;
    return true;
  });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => {
    const ae = (a.effectiveFrom || a.fromDate || '').localeCompare(b.effectiveFrom || b.fromDate || '');
    return -ae;
  });
  return candidates[0] ?? null;
}

function isoYearWeek(weekStartYmd: string): { yearNo: number; weekNo: number } {
  const d = parseLocalDate(weekStartYmd);
  d.setHours(12, 0, 0, 0);
  return { yearNo: getISOWeekYear(d), weekNo: getISOWeek(d) };
}

export function isWeeklyOrMonthlySalaried(
  activePayRecord: PayRecord | undefined,
  paymentTypeNameById: Map<number, string>,
  paymentFreqNameById: Map<number, string>
): boolean {
  if (!activePayRecord?.isActive) return false;
  const pt = (paymentTypeNameById.get(activePayRecord.paymentTypeId) || '').trim().toLowerCase();
  const fid = activePayRecord.paymentFrequencyId;
  const pf = fid != null ? (paymentFreqNameById.get(fid) || '').trim().toLowerCase() : '';
  return pt === 'salary' && (pf === 'weekly' || pf === 'monthly');
}

/**
 * Builds one row per week that has approved timecard data, chronologically.
 * Cumulative "Hours in Lieu Due" runs over all such weeks; then rows are filtered to [fromWeekStart, toWeekStart].
 */
export function buildStaffSalariedTable(
  employeeDisplay: string,
  timecards: Timecard[],
  payRecords: PayRecord[],
  fromWeekStart: string,
  toWeekStart: string
): { displayRows: StaffSalariedReportRow[]; allRows: StaffSalariedReportRow[] } {
  const from = fromWeekStart.slice(0, 10);
  const to = toWeekStart.slice(0, 10);

  const approved = (timecards || []).filter((tc) => tc.isApproved === true);
  const byWeek = new Map<string, number>();

  for (const tc of approved) {
    const key = (tc.weekStartDate || '').slice(0, 10);
    if (!key) continue;
    const hrs = sumHoursWorkedFromTimecard(tc);
    byWeek.set(key, (byWeek.get(key) || 0) + hrs);
  }

  const weekKeys = Array.from(byWeek.keys()).sort((a, b) => a.localeCompare(b));

  let cumulative = 0;
  const allRows: StaffSalariedReportRow[] = [];

  for (const weekStartDate of weekKeys) {
    const hoursWorked = Math.round(byWeek.get(weekStartDate)! * 100) / 100;
    const pr = getApplicablePayRecordForWeek(payRecords, weekStartDate);
    const setAgainst = Math.round((pr?.contractedHours ?? 0) * 100) / 100;
    const hoursUnderOver = Math.round((hoursWorked - setAgainst) * 100) / 100;
    cumulative = Math.round((cumulative + hoursUnderOver) * 100) / 100;
    const { yearNo, weekNo } = isoYearWeek(weekStartDate);

    allRows.push({
      employeeDisplay,
      yearNo,
      weekNo,
      weekStartDate,
      hoursWorked,
      setAgainstHours: setAgainst,
      hoursUnderOver,
      hoursInLieuDue: cumulative,
    });
  }

  const displayRows = allRows.filter((r) => r.weekStartDate >= from && r.weekStartDate <= to);

  return { allRows, displayRows };
}

export function staffSalariedRowsToGrid(rows: StaffSalariedReportRow[]): {
  headers: string[];
  stringRows: string[][];
} {
  const headers = [
    'Employee',
    'Year',
    'Week no.',
    'Hours worked',
    'Set against hours',
    'Hours under/over',
    'Hours in lieu due',
  ];
  const stringRows = rows.map((r) => [
    r.employeeDisplay,
    String(r.yearNo),
    String(r.weekNo),
    r.hoursWorked.toFixed(2),
    r.setAgainstHours.toFixed(2),
    r.hoursUnderOver.toFixed(2),
    r.hoursInLieuDue.toFixed(2),
  ]);
  return { headers, stringRows };
}
